import { Component, HostListener, inject, signal } from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { distinctUntilChanged, map } from "rxjs";
import { DeviceDetectorService } from "ngx-device-detector";
import { CabinetTable } from "@bitwarden/web-vault/app/components/tables/cabinet-table/cabinet.table";
import { TablesFactory } from "@bitwarden/web-vault/app/components/tables/tables.factory";
import { TablesType } from "@bitwarden/web-vault/app/models/enum/tables.enum";
import { IButton } from "@bitwarden/web-vault/app/models/interfaces/button.interface";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { CabinetFileUploadedData } from "@bitwarden/web-vault/app/models/types/cabinet.types";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import {
  clearDeepLinkQueryParam,
  deepLinkParamReady$,
} from "@bitwarden/web-vault/app/shared/utils/deep-link-selection.util";

// NOTE: global side-effect import (not styleUrls) to match the repo-wide convention —
// no component here uses styleUrls; the custom webpack build loads component SCSS this way.
import "./cabinet.component.scss";

@Component({
  selector: "cabinet",
  templateUrl: "cabinet.component.html",
  providers: [TablesFactory],
})
export class CabinetComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly document = inject(DOCUMENT);
  private readonly appState = inject(AppStateStoreService);
  protected readonly deviceDetectorService = inject(DeviceDetectorService);
  protected readonly table: CabinetTable = inject(TablesFactory).get(TablesType.Cabinet, this);

  // Route params are available immediately, but on a cold load (bookmark, refresh, pasted link)
  // the cabinet store hasn't populated yet. combineLatest with the table's data (rather than a
  // one-shot queryParams subscription) covers both timings (#4519 follow-up).
  private readonly fileId$ = this.route.queryParams.pipe(
    map((params) => params["fileId"] as string | undefined),
    distinctUntilChanged(),
  );

  // Plain IButton object (not `new ActionButton()`) so no `class` gets defaulted to "primary" —
  // matches the Budget page's "Create new" button exactly (same fullClass/fontClass pattern).
  protected readonly uploadNewFileOptions: IButton = {
    text: this.i18n.t("uploadNewFile"),
    icon: "upload-file",
    iconClass: "tw-h-4 tw-w-4 tw-text-white",
    fullClass:
      "tw-shadow-manage-accounts tw-flex tw-h-12 tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-4 tw-bg-dt-custom-teal-11",
    isEnabled: true,
    onClick: () => this.uploadNewFile(),
  };

  // State management for upload dialog
  protected showUploadDialog = false;
  protected uploadDialogTitle = "uploadNewFile";

  // Mobile metric-pill paging: 4 pills, 3 visible at a time, one page forward/back.
  protected readonly pillsPageCount = 4;
  protected readonly pillsPageSize = 3;
  protected readonly maxPillsOffset = this.pillsPageCount - this.pillsPageSize;
  protected readonly pillsOffset = signal(0);

  protected isPillVisible(index: number): boolean {
    const offset = this.pillsOffset();
    return index >= offset && index < offset + this.pillsPageSize;
  }

  protected shiftPills(delta: number): void {
    this.pillsOffset.update((value) => Math.max(0, Math.min(this.maxPillsOffset, value + delta)));
  }

  constructor() {
    deepLinkParamReady$(this.fileId$, toObservable(this.table.data))
      .pipe(takeUntilDestroyed())
      .subscribe((fileId) => {
        const files = this.table.data() ?? [];
        const matchingFile = files.find((data) => data.fileAndAccount.file?.id === fileId);
        if (matchingFile?.fileAndAccount.file) {
          if (this.table.selectedFile()?.id !== matchingFile.fileAndAccount.file.id) {
            this.table.onRowClicked(matchingFile.fileAndAccount.file);
          }
        } else {
          // No accompanying clear on a match: the search bar is the only live producer of this
          // param and it already resolved the file against the store, so a matched fileId should
          // stay in the URL (copy/shareable), matching pre-#4519 observable behaviour. Only a
          // genuinely stale id (the data is confirmed loaded here — deepLinkParamReady$ only
          // emits once loaded$ is an array) gets swept from the URL.
          this.clearFileIdFromUrl();
        }
      });
  }

  /** Drop a stale deep-link query param once we're sure the file it names doesn't exist. */
  private clearFileIdFromUrl(): void {
    clearDeepLinkQueryParam(this.router, this.route, "fileId");
  }

  uploadNewFile(): void {
    this.showUploadDialog = true;
  }

  @HostListener("window:keydown.escape")
  onEscapeKey(): void {
    // An open inner overlay should consume Escape itself — don't also close the upload form.
    // Covers CDK overlays (date picker, dropdown panels) and the file full-preview. This window
    // listener is registered before the inner ones, so it can't rely on them stopping
    // propagation; it gates on the overlay being present instead.
    if (this.document.querySelector(".cdk-overlay-pane, .file-full-preview")) {
      return;
    }
    if (this.appState.activeProcesses.has("statement-import")) {
      return;
    }
    if (this.showUploadDialog) {
      this.closeUploadForm();
    }
  }

  closeUploadForm(_data?: CabinetFileUploadedData): void {
    this.showUploadDialog = false;
    // Scroll lock is released by [appScrollLock] when the overlay is removed.
    // Table will auto-refresh through its subscription when new file is added
  }

  handleFormStepChange(_step: string): void {
    // Update dialog title based on the current step if needed
    this.uploadDialogTitle = "uploadNewFile";
  }

  closeCabinetDetails() {
    this.table.clearSelection();
  }
}
